import { useGetList } from "ra-core";

import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

export interface TaskArea {
  id: number;
  key: string;
  name: string;
  position: number;
  active: boolean;
}

export interface ChecklistItem {
  id: number;
  task_id: number;
  label: string;
  position: number;
  done_at: string | null;
  done_by: number | null;
}

export interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
}

export const OPEN_STATUSES = ["todo", "in_progress", "waiting"];

export const nativeSelectClass =
  "border-input dark:bg-input/30 h-10 md:h-9 w-full rounded-md border bg-transparent px-3 text-base md:text-sm";

export const useTaskAreas = () =>
  useGetList<TaskArea>("task_areas", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "position", order: "ASC" },
  });

export const useTeam = () =>
  useGetList<TeamMember>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { disabled: false, kind: "human" },
  });

export const fullName = (member?: TeamMember) =>
  member ? `${member.first_name} ${member.last_name}`.trim() : "";

// Steps appended after the existing ones, in one insert: on a task
// (author and date of a ticked step are set by the database, D-028) or on
// a checklist template.
const insertSteps = async (
  table: "task_checklist_items" | "checklist_template_items",
  parent: Record<string, number>,
  labels: string[],
  startPosition: number,
) => {
  if (labels.length === 0) return;
  const { error } = await getSupabaseClient()
    .from(table)
    .insert(
      labels.map((label, index) => ({
        ...parent,
        label,
        position: startPosition + index,
      })),
    );
  if (error) throw new Error(error.message);
};

export const addChecklistSteps = (
  taskId: number,
  labels: string[],
  startPosition: number,
) =>
  insertSteps(
    "task_checklist_items",
    { task_id: taskId },
    labels,
    startPosition,
  );

export const addTemplateSteps = (
  templateId: number,
  labels: string[],
  startPosition: number,
) =>
  insertSteps(
    "checklist_template_items",
    { template_id: templateId },
    labels,
    startPosition,
  );

export interface ChecklistTemplate {
  id: number;
  key: string;
  name: string;
  description: string | null;
  area_id: number | null;
  default_assignee_id: number | null;
  procedure_id: number | null;
  active: boolean;
}

export interface TemplateItem {
  id: number;
  template_id: number;
  label: string;
  position: number;
}

export interface Procedure {
  id: number;
  key: string;
  title: string;
  body_md: string;
  scope: "process" | "stage";
}

export const useChecklistTemplates = () =>
  useGetList<ChecklistTemplate>("checklist_templates", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });

export const useProcedures = () =>
  useGetList<Procedure>("procedures", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "title", order: "ASC" },
  });

// A task from a template, through tenax_create_task_from_template (D-030):
// the database copies the steps and records the template.
export const createTaskFromTemplate = async (args: {
  templateId: number;
  title?: string;
  assignee?: number | null;
  dueDate?: string | null;
  priority?: number;
  areaId?: number | null;
}) => {
  const { data, error } = await getSupabaseClient().rpc(
    "tenax_create_task_from_template",
    {
      p_template_id: args.templateId,
      p_title: args.title ?? null,
      p_assignee: args.assignee ?? null,
      p_due_date: args.dueDate ?? null,
      p_priority: args.priority ?? 2,
      p_area_id: args.areaId ?? null,
    },
  );
  if (error) throw new Error(error.message);
  return data as { id: number };
};

// A date input value (yyyy-mm-dd) as the start of that day, local time,
// which is how Atomic's task lists bucket due dates.
export const dateInputToIso = (value: string): string | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toISOString();
};

export const isoToDateInput = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Key of an area, template or procedure from its name: lowercase ascii with
// underscores; a clash is reported by the unique constraint.
export const keyFromName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
